import React, {useEffect} from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import demoImage from "../assets/demo-image.png";
import { analytics } from "../firebaseConfig";
import { logEvent } from "firebase/analytics";
const LandingPage = () => {
  useEffect(()=>{
    logEvent(analytics, 'landing_page');
  },[])
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-950 to-black text-white">
      <Helmet>
        <title>AI Contract Manager | Automate Contract Review & Summaries</title>
        <meta
          name="description"
          content="AI Contract Manager helps your team upload, extract, and summarize key contract terms using AI. Built by Clairvoyant Inc. to streamline legal workflows."
        />
        <link rel="canonical" href="https://aicontractmanager.com/" />
        <meta property="og:title" content="AI Contract Manager | Automate Contract Review & Summaries" />
        <meta
          property="og:description"
          content="Upload contracts and instantly extract key terms with AI. A powerful tool for legal, finance, and procurement teams."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://aicontractmanager.com/" />
      </Helmet>

      {/* HERO */}
      <section className="py-24 px-6 text-center">
        <h1 className="text-5xl md:text-6xl font-extrabold mb-4 text-blue-500 drop-shadow-lg">
          Drowning in Contracts?
        </h1>
        <p className="text-xl text-gray-300 max-w-2xl mx-auto mb-6">
          Let AI handle the paperwork. Summarize, organize, and collaborate on contracts — in seconds, not hours.
        </p>

        <div className="flex flex-col sm:flex-row justify-center gap-4 mb-10">
          <Link
            to="/signup"
            className="bg-blue-600 hover:bg-blue-700 transition px-8 py-3 text-lg rounded-lg font-semibold shadow-lg"
          >
            Try It Free
          </Link>
          <Link
            to="/login"
            className="border border-blue-500 text-blue-300 hover:bg-blue-600 hover:text-white transition px-8 py-3 text-lg rounded-lg font-semibold"
          >
            Log In
          </Link>
        </div>

        {/* UI FLOW + SCREENSHOT SIDE-BY-SIDE */}
        <section className="px-6 mt-12">
  <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-10 items-start">
    
    {/* LEFT: Flow + Heading */}
    <div className="flex flex-col gap-4">
      <div>
        <h5 className="text-3xl font-semibold text-blue-500 tracking-wide mb-2">
          Instantly Extract Information
        </h5>
        <h3 className="text-2xl font-bold text-white">Lease Example:</h3>
      </div>

      <div className="bg-slate-800/40 border border-slate-700 rounded-lg p-6 text-left text-sm sm:text-base font-mono text-gray-300 space-y-2">
        <div>📄 Upload your agreement...</div>
        <div>🤖 Extracting key terms with AI...</div>
        <div>✅ Found: Effective Date – April 3, 2025</div>
        <div>✅ Found: Monthly Rent – $6,000</div>
        <div>✅ Found: Parties – Hugh Rentmore & Ned A. Home</div>
        <div>✅ Found: Renewal Terms – Month-to-Month</div>
        <div className="pt-2 text-green-400">
          📝 All fields editable. Ready to review and save.
        </div>
      </div>
    </div>

    {/* RIGHT: Screenshot */}
    <div className="rounded-xl overflow-hidden border border-slate-700 shadow-lg">
      <img
        src={demoImage}
        alt="AI Contract Manager Demo"
        className="w-full h-auto object-contain max-h-[600px]"
      />
    </div>
  </div>
</section>
      </section>

      {/* FEATURES */}
      <section className="px-6 py-20">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700 shadow hover:shadow-blue-700/30 transition">
            <h3 className="text-xl font-semibold text-blue-400 mb-2">🧠 AI Contract Summaries</h3>
            <p className="text-gray-300">Instantly extract key dates, obligations, and clauses from your uploaded documents.</p>
          </div>
          <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700 shadow hover:shadow-blue-700/30 transition">
            <h3 className="text-xl font-semibold text-blue-400 mb-2">📁 Secure Cloud Storage</h3>
            <p className="text-gray-300">Store all your legal documents in one centralized location. Encrypted and accessible.</p>
          </div>
          <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700 shadow hover:shadow-blue-700/30 transition">
            <h3 className="text-xl font-semibold text-blue-400 mb-2">👥 Team Collaboration</h3>
            <p className="text-gray-300">Add teammates, assign roles, and manage documents together with ease.</p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default LandingPage;
